const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8081";

export const API_ROOT = `${API_BASE}/api`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function jitter(ms) {
  const n = Number(ms) || 0;
  if (n <= 0) return 0;
  const spread = n * 0.2;
  return Math.max(0, Math.round(n - spread + Math.random() * spread * 2));
}

const isRetryableStatus = (status) =>
  [408, 425, 429, 500, 502, 503, 504, 520, 522, 524].includes(Number(status));

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

export class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

function isNetworkError(err) {
  return err instanceof TypeError || /network|fetch|failed/i.test(String(err?.message || ""));
}

/** Mặc định retry âm thầm (cold start / mạng chập): ~≤30s tổng backoff. */
const DEFAULT_RETRY = {
  retries: 5,
  baseDelayMs: 650,
  maxDelayMs: 5500,
};

/**
 * request("/auth/me", { token })
 * request("/users/me/avatar", { token, body: formData, isFormData: true })
 *
 * Retry im lặng (không toast):
 * - GET/HEAD: lỗi mạng + HTTP 5xx/429… có thể retry.
 * - POST/PUT/PATCH/DELETE: chỉ retry khi lỗi mạng (tránh gửi lại body khi server đã nhận).
 * - Tuỳ chọn: `retry: { retries, baseDelayMs, maxDelayMs, allowNonIdempotent: true }` cho POST cần retry HTTP.
 */
export async function request(
  path,
  { method = "GET", token, body, headers, isFormData = false, retry: retryCfg } = {}
) {
  const url = path.startsWith("http") ? path : `${API_ROOT}${path}`;
  const m = String(method || "GET").toUpperCase();
  const isGetLike = m === "GET" || m === "HEAD";
  const allowNonIdempotent = Boolean(retryCfg?.allowNonIdempotent);
  const shouldRetryHttp = isGetLike || allowNonIdempotent;

  const retries = Math.max(0, Number(retryCfg?.retries ?? DEFAULT_RETRY.retries) || 0);
  const baseDelayMs = Math.max(0, Number(retryCfg?.baseDelayMs ?? DEFAULT_RETRY.baseDelayMs) || 0);
  const maxDelayMs = Math.max(0, Number(retryCfg?.maxDelayMs ?? DEFAULT_RETRY.maxDelayMs) || 0);

  const h = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers || {}),
  };

  const fetchOpts = {
    method: m,
    headers: h,
    ...(body === undefined ? {} : { body: isFormData ? body : JSON.stringify(body) }),
  };

  let lastErr = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, fetchOpts);
      const data = await readJsonSafe(res);

      if (!res.ok) {
        const msg = data?.message || "Lỗi từ server";
        const err = new ApiError(msg, { status: res.status, data });

        const canRetryHttp =
          attempt < retries && shouldRetryHttp && isRetryableStatus(res.status);
        if (canRetryHttp) {
          lastErr = err;
          const wait = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
          await sleep(jitter(wait));
          continue;
        }
        throw err;
      }

      return data;
    } catch (err) {
      if (err instanceof ApiError) throw err;

      const isNetwork = isNetworkError(err);
      const canRetryNet = attempt < retries && isNetwork;
      if (canRetryNet) {
        lastErr = err;
        const wait = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
        await sleep(jitter(wait));
        continue;
      }

      throw err;
    }
  }

  if (lastErr instanceof ApiError) throw lastErr;
  throw lastErr || new Error("Request failed");
}

let apiPrewarmed = false;

/**
 * Gọi một lần khi app mở: GET public nhẹ qua cùng stack `request()` (retry âm thầm),
 * không toast — giảm cold start trước khi user vào admin / chi tiết.
 */
export function prewarmApi() {
  if (typeof window === "undefined" || apiPrewarmed) return;
  apiPrewarmed = true;
  request("/settings").catch(() => {});
}
